import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ContentHeader from 'modules/views/shared/Wizard/ContentHeader';
import NavigationButtons from 'modules/views/shared/NavigationButtons';
import { ReactComponent as ProjectAcceptedIcon } from 'assets/icons/ProjectAccepted.svg';

import { useConsentFormContext } from '../context/ConsentFormContext';
import { hasWorkerAcceptedTheProject } from 'utils/onboarding';
import { useConsentFormSkippedFieldsCheck, useBadgeSkippableFieldsCheck } from '../hooks/useSkippedFieldsCheck';
import { WIZARD_STEPS, StepKey } from 'modules/views/shared/Wizard/config';
import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { resetOnboardingState, setHasReachedConsentForm } from 'modules/state-mgmt/onboarding';
import { selectCustomConsentAgreements } from 'modules/state-mgmt/cache';
import { useStyles } from '../styles';
import { ROUTES } from 'constants/routes';
import { ProjectInfo, SkippedFieldsModal, OptionalBadgeFieldsModal } from './ProjectInformationComponents';

// Map screen identifier to wizard step key
const getStepKeyFromScreen = (screen: string): StepKey | null => {
  const screenMap: Record<string, StepKey> = {
    personal: StepKey.PERSONAL_INFORMATION,
    contact: StepKey.CONTACT_INFORMATION,
    trades: StepKey.TRADES,
  };
  return screenMap[screen] || null;
};

// Navigate to the wizard step for a given screen
const navigateToScreen = (screen: string | null, navigate: ReturnType<typeof useNavigate>) => {
  if (!screen) return;

  const targetStepKey = getStepKeyFromScreen(screen);
  if (targetStepKey) {
    const step = WIZARD_STEPS.find(s => s.key === targetStepKey);
    if (step) {
      navigate(step.route);
    }
  }
};

export const ProjectInformationPage = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    nextStep,
    prevStep,
    projectName,
    projectAddress,
    projectId,
    assignedCompanies,
    isArchivedProject,
    addEmployerFlow,
    isWorker,
  } = useConsentFormContext();

  const unsignedCustomForms = useAppSelector(state => selectCustomConsentAgreements(state, projectId));
  // Same definition the sibling consent-form components use: either the device is offline or this
  // worker record was captured offline and is still awaiting upload.
  const isOffline = useAppSelector(
    state => !state.app.isOnline || !!state.onboarding?.uploadingDetails?.isOffline,
  );

  const [showSkippedFieldsModal, setShowSkippedFieldsModal] = useState(false);
  const [showBadgeSkippableModal, setShowBadgeSkippableModal] = useState(false);

  const workerAcceptedTheProject = hasWorkerAcceptedTheProject(assignedCompanies);
  const isProjectCompleted = workerAcceptedTheProject || isArchivedProject;

  // Check for ConsentForm skippable fields
  const { skippedFieldNames: consentFormSkippedFields, earliestScreen: consentFormEarliestScreen } =
    useConsentFormSkippedFieldsCheck();

  // Check for Badge Printing/Activation skippable fields
  const {
    skippedFieldNames: badgeSkippedFieldNames,
    skippedFields: badgeSkippedFields,
    earliestScreen: badgeEarliestScreen,
  } = useBadgeSkippableFieldsCheck();

  // Handle back button in consent form modal - navigate to earliest screen with missing fields
  // This makes ConsentForm skippable fields mandatory
  const handleNavigateToConsentFormFields = useCallback(() => {
    setShowSkippedFieldsModal(false);
    dispatch(setHasReachedConsentForm(true));
    navigateToScreen(consentFormEarliestScreen, navigate);
  }, [consentFormEarliestScreen, navigate, dispatch]);

  // Handle back button in badge skippable modal - navigate to earliest screen with missing fields
  // Note: Fields remain optional, no validation errors shown
  const handleNavigateToBadgeFields = useCallback(() => {
    setShowBadgeSkippableModal(false);
    navigateToScreen(badgeEarliestScreen, navigate);
  }, [badgeEarliestScreen, navigate]);

  const onBack = () => prevStep();

  const onNext = () => {
    // If project is already accepted or archived, navigate to done state
    if (isProjectCompleted) {
      if (addEmployerFlow && unsignedCustomForms.length > 0) {
        // In addEmployerFlow with unsigned custom forms: proceed to sign them
        dispatch(setHasReachedConsentForm(true));
        nextStep();
      } else if (isOffline && !isWorker) {
        // Offline admin: My Projects is a worker-facing, online-only page, so send them back to
        // Select Project — the admin's offline entry point — rather than a screen that cannot load.
        dispatch(resetOnboardingState());
        navigate(ROUTES.SELECT_PROJECT.path);
      } else if (addEmployerFlow || isWorker) {
        navigate(ROUTES.PROJECT_LIST.path);
      } else {
        dispatch(resetOnboardingState());
        navigate(ROUTES.SELECT_PROJECT.path);
      }
      return;
    }

    // Check for ConsentForm skippable fields (mandatory)
    if (consentFormSkippedFields.length > 0) {
      setShowSkippedFieldsModal(true);
      return;
    }

    // Check for Badge Printing/Activation skippable fields (optional nudge)
    if (badgeSkippedFieldNames.length > 0) {
      dispatch(setHasReachedConsentForm(true));
      setShowBadgeSkippableModal(true);
      return;
    }

    // All checks passed, proceed to next step
    dispatch(setHasReachedConsentForm(true));
    nextStep();
  };

  // Handle continuing to consent form even with badge skippable fields
  const handleContinueToConsentForm = useCallback(() => {
    setShowBadgeSkippableModal(false);
    nextStep();
  }, [nextStep]);

  return (
    <div>
      <ContentHeader title={t('consentForm.step1.title')} />
      <div className={classes.headerSpacer} />
      <ProjectInfo
        title={t('consentForm.step1.projectName')}
        value={projectName}
        className={classes.projectInfoContainer}
        labelClass={classes.label}
        valueClass={classes.valueText}
      />
      <ProjectInfo
        title={t('consentForm.step1.projectAddress')}
        value={projectAddress}
        className={classes.projectInfoContainer}
        labelClass={classes.label}
        valueClass={classes.valueText}
      />
      {workerAcceptedTheProject && (
        <div className={classes.projectAcceptedContainer}>
          <ProjectAcceptedIcon />
          <p className={classes.projectAcceptedText}>{t('consentForm.step1.projectAccepted')}</p>
        </div>
      )}
      <NavigationButtons
        secondaryButtonProps={{ onClick: onBack, testId: 'consent-project-back-button' }}
        primaryButtonProps={{
          label: isProjectCompleted && !(addEmployerFlow && unsignedCustomForms.length > 0) ? t('common.done') : t('consentForm.step1.next'),
          mobileLabel: isProjectCompleted && !(addEmployerFlow && unsignedCustomForms.length > 0) ? t('common.done') : t('common.next'),
          hasEndArrow: true,
          styleClass: classes.fillButton,
          testId: 'consent-project-next-button',
          onClick: onNext,
        }}
      />
      <SkippedFieldsModal
        isVisible={showSkippedFieldsModal}
        fieldNames={consentFormSkippedFields}
        onNavigate={handleNavigateToConsentFormFields}
        onClose={() => setShowSkippedFieldsModal(false)}
        classes={classes}
        t={t}
      />
      <OptionalBadgeFieldsModal
        isVisible={showBadgeSkippableModal}
        skippedFields={badgeSkippedFields}
        onGoBack={handleNavigateToBadgeFields}
        onContinue={handleContinueToConsentForm}
        onClose={() => setShowBadgeSkippableModal(false)}
        classes={classes}
        t={t}
      />
    </div>
  );
};
